import { Injectable } from '@angular/core';
import { filter } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AudioNotificationType } from '@nimbus-assistant/enums/audio-notification-type';
import { Modality } from '@nimbus-assistant/models/task-types.model';
import { AudioService } from './audio.service';
import { UserSettingsService } from './user-settings.service';
import { AssistantTasksService } from './assistant-tasks.service';
import { AuthService } from './auth.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class TaskAudioNotificationsService {
  private audioFile: string;
  protected audioType: AudioNotificationType;

  public constructor(
    private readonly authService: AuthService,
    private readonly audioService: AudioService,
    private readonly userSettingsService: UserSettingsService,
    private readonly tasksService: AssistantTasksService,
  ) {
    this.initialize();
  }

  public play(audioType: AudioNotificationType): void {
    this.stop();
    this.audioType = audioType;
    this.audioFile = this.getAudioFile();
    if (this.audioFile) {
      this.audioService.playAudio(this.audioType, this.audioFile, true);
    }
  }

  public stop(): void {
    this.audioService.stopAudio(this.audioType);
  }

  public initialize(): void {
    this.authService.authenticated$
      .pipe(
        filter((x) => !x),
        untilDestroyed(this),
      )
      .subscribe((_) => this.stop());

    this.tasksService.incomingTask$.pipe(untilDestroyed(this)).subscribe((task) => {
      if (task) {
        this.playModalityNotification(task.modality);
      } else {
        this.stop();
      }
    });
  }

  private getAudioFile(): string {
    const settings = this.userSettingsService.userSettings;
    switch (this.audioType) {
      case AudioNotificationType.ExternalTask:
        return settings.useAudioForExternalTasks ? settings.audioForExternalTasks : null;
      case AudioNotificationType.InstantMessage:
        return settings.useAudioForInstantMessages ? settings.audioForInstantMessages : null;
      case AudioNotificationType.Email:
        return settings.useAudioForEmails ? settings.audioForEmails : null;
      default:
        return null;
    }
  }

  private playModalityNotification(modality: string): void {
    let notification = null;
    if (modality === Modality.IM) {
      notification = AudioNotificationType.InstantMessage;
    }
    if (modality === Modality.ExternalTask) {
      notification = AudioNotificationType.ExternalTask;
    }
    if (modality === Modality.Email) {
      notification = AudioNotificationType.Email;
    }

    if (notification) {
      this.play(notification);
    }
  }
}
